import { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  getClientDetailsByUserIdAndDatetime,
  getUserChatByDateRange,
} from "../../Api_Qdrant/Fast_apis";

import { getUserBotChatDates } from "../../Apis_chatbot/apis";

import { getFastApiErrorMessage } from "../../Api_Qdrant/fastApiError";

import { ChatRoles } from "../../app/Enums/ChatRoles";

import MarkdownMessage from "../../app/components/MarkdownMessage/MarkdownMessage";

import "./ClientDetails.css";

/* =========================================================
   DATE HELPERS
========================================================= */

function formatDate(iso) {
  if (!iso) return "—";

  const d = new Date(iso);

  if (Number.isNaN(d.getTime())) {
    return "—";
  }

  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDateOnly(date) {
  if (!date) return "";

  // If API sends:
  // 2026-08-21T00:00:00
  // or
  // 2026-08-21
  //
  // simply take the date portion.
  if (typeof date === "string") {
    const match = date.match(/^(\d{4}-\d{2}-\d{2})/);

    if (match) {
      return match[1];
    }
  }

  const d = new Date(date);

  if (Number.isNaN(d.getTime())) {
    return "";
  }

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function formatReadableDate(date) {
  if (!date) return "—";

  const d = new Date(`${date}T00:00:00`);

  if (Number.isNaN(d.getTime())) {
    return "—";
  }

  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatLongDate(date) {
  if (!date) return "—";

  const d = new Date(`${date}T00:00:00`);

  if (Number.isNaN(d.getTime())) {
    return "—";
  }

  return d.toLocaleDateString("en-IN", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function formatTimeOnly(iso) {
  if (!iso) return "";

  const d = new Date(iso);

  if (Number.isNaN(d.getTime())) {
    return "";
  }

  return d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/* =========================================================
   ROLE HELPERS
========================================================= */

function getRoleName(roleId) {
  return ChatRoles[roleId] || "Unknown";
}

function isUserMessage(roleId) {
  return roleId === ChatRoles.User;
}

function isBotMessage(roleId) {
  return roleId === ChatRoles.Bot;
}

function isAgentMessage(roleId) {
  return roleId === ChatRoles.Agent;
}

/* =========================================================
   PAGE
========================================================= */

export default function ClientDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();

  /*
   * Expected navigation:
   *
   * navigate("/client-details", {
   *   state: {
   *     userId: item.user_id,
   *     detectionDate: item.created_at,
   *   },
   * });
   */

  const userId = location.state?.userId;
  const detectionDate = location.state?.detectionDate;

  /* =======================================================
     CLIENT STATE
  ======================================================= */

  const [client, setClient] = useState(null);
  const [loadingClient, setLoadingClient] = useState(true);
  const [clientError, setClientError] = useState(false);

  /*
   * Tracks which client-info fields (name/contact/requirement/
   * detected-at) are click-expanded to show their full value.
   * Keyed by field name; collapsed by default.
   */
  const [expandedFields, setExpandedFields] = useState({});

  const toggleFieldExpanded = (key) => {
    setExpandedFields((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  };

  /* =======================================================
     CHAT STATE
  ======================================================= */

  const [messages, setMessages] = useState([]);

  const [selectedDate, setSelectedDate] = useState(null);

  const [loadingChat, setLoadingChat] = useState(false);
  const [chatError, setChatError] = useState(false);

  const [chatPage, setChatPage] = useState(1);
  const [chatTotalPages, setChatTotalPages] = useState(1);

  /* =======================================================
     OTHER CHAT DATES

     IMPORTANT:
     This API is NOT called during initial page loading.
     It is only called after clicking "View other chat dates".
  ======================================================= */

  const [chatDates, setChatDates] = useState([]);
  const [showOtherDates, setShowOtherDates] = useState(false);
  const [loadingDates, setLoadingDates] = useState(false);
  const [datesLoaded, setDatesLoaded] = useState(false);

  /* =======================================================
     CHAT SCROLL REFS
  ======================================================= */

  const messagesContainerRef = useRef(null);

  /*
   * Used when older messages are inserted at the TOP.
   */
  const previousScrollHeightRef = useRef(0);

  /*
   * Used together with previousScrollHeightRef
   * to preserve the exact position where the user was reading.
   */
  const previousScrollTopRef = useRef(0);

  /*
   * Prevents multiple page requests while the
   * current older-message request is running.
   */
  const isLoadingOlderRef = useRef(false);

  /* =======================================================
     DERIVED VALUES
  ======================================================= */

  const detectionDay = formatDateOnly(detectionDate);

  const isDetectionDate = selectedDate === detectionDay;

  /* =========================================================
     1. LOAD CLIENT DETAILS
  ========================================================= */

  useEffect(() => {
    let mounted = true;

    const loadClient = async () => {
      setLoadingClient(true);
      setClientError(false);

      if (!userId || !detectionDate) {
        console.error("Missing client navigation data:", {
          userId,
          detectionDate,
        });

        if (mounted) {
          setClient(null);
          setClientError(true);
          setLoadingClient(false);
        }

        return;
      }

      try {
        const res = await getClientDetailsByUserIdAndDatetime(
          userId,
          detectionDate,
          5,
        );

        if (!mounted) return;

        if (res?.success && res?.data) {
          setClient(res.data);
          setClientError(false);
        } else {
          setClient(null);
          setClientError(true);

          if (!res?.rateLimited) {
            toast.error(
              getFastApiErrorMessage(
                res,
                "Client details could not be found.",
              ),
            );
          }
        }
      } catch (error) {
        console.error("Failed to load client details:", error);

        if (!mounted) return;

        setClient(null);
        setClientError(true);

        toast.error(
          getFastApiErrorMessage(error, "Unable to load client details."),
        );
      } finally {
        if (mounted) {
          setLoadingClient(false);
        }
      }
    };

    loadClient();

    return () => {
      mounted = false;
    };
  }, [userId, detectionDate]);

  /* =========================================================
     2. LOAD CHAT FOR DATE

     BACKEND ORDER:
     DESC = newest → oldest

     UI ORDER:
     ASC = oldest → newest

     Therefore every API page is reversed before
     being displayed.

     Example:

     Backend page 1:
     10:00
     09:59
     09:58
     ...
     09:11

     UI page 1:
     09:11
     09:12
     ...
     09:59
     10:00
  ========================================================= */

  const loadChatForDate = useCallback(
    async (date, page = 1, mode = "replace") => {
      if (!userId || !date) {
        return;
      }

      setLoadingChat(true);
      setChatError(false);

      try {
        const res = await getUserChatByDateRange(
          userId,
          date,
          date,
          page,
          50,
        );

        if (!res?.success) {
          setChatError(true);

          if (mode === "replace") {
            setMessages([]);
          }

          return;
        }

        /*
         * IMPORTANT:
         *
         * Backend returns DESC.
         *
         * Reverse ONLY this page.
         *
         * Do NOT reverse the complete messages array.
         */
        const newMessages = [
          ...(res?.data?.items || []),
        ].reverse();

        const totalPages = res?.data?.total_pages || 1;

        setChatTotalPages(totalPages);
        setChatPage(page);

        if (mode === "appendOlder") {
          /*
           * Because backend page 2 is older than page 1,
           * after reversing page 2 it must be placed
           * BEFORE the existing messages.
           *
           * Example:
           *
           * Existing:
           * 09:11 ... 10:00
           *
           * Older page:
           * 09:01 ... 09:10
           *
           * Result:
           * 09:01 ... 09:10 09:11 ... 10:00
           */
          setMessages((previous) => [
            ...newMessages,
            ...previous,
          ]);
        } else {
          /*
           * Initial load / selected date load.
           */
          setMessages(newMessages);
        }
      } catch (error) {
        console.error("Failed to load conversation:", error);

        setChatError(true);

        if (mode === "replace") {
          setMessages([]);
        }
      } finally {
        setLoadingChat(false);
      }
    },
    [userId],
  );

  /* =========================================================
     3. INITIAL DETECTION-DAY CHAT

     This runs only after client has been successfully loaded.
  ========================================================= */

  useEffect(() => {
    if (!userId || !detectionDay || clientError) {
      return;
    }

    setSelectedDate(detectionDay);

    setChatPage(1);

    setChatTotalPages(1);

    previousScrollHeightRef.current = 0;
    previousScrollTopRef.current = 0;

    loadChatForDate(
      detectionDay,
      1,
      "replace",
    );
  }, [
    userId,
    detectionDay,
    clientError,
    loadChatForDate,
  ]);

  /* =========================================================
     4. SCROLL HANDLING AFTER CHAT LOAD

     Initial/date load:
       → scroll to bottom

     Older page load:
       → preserve exact reading position
  ========================================================= */

  useEffect(() => {
    if (loadingChat || messages.length === 0) {
      return;
    }

    const container = messagesContainerRef.current;

    if (!container) {
      return;
    }

    /*
     * Older messages were inserted at the TOP.
     *
     * scrollHeight increased.
     *
     * Example:
     *
     * Before:
     * scrollHeight = 2000
     * scrollTop    = 300
     *
     * After adding older messages:
     * scrollHeight = 3000
     *
     * Difference = 1000
     *
     * New scrollTop:
     * 300 + 1000 = 1300
     *
     * Therefore the same messages remain
     * visible to the user.
     */
    if (previousScrollHeightRef.current > 0) {
      const newScrollHeight = container.scrollHeight;

      const heightDifference =
        newScrollHeight -
        previousScrollHeightRef.current;

      container.scrollTop =
        previousScrollTopRef.current +
        heightDifference;

      previousScrollHeightRef.current = 0;
      previousScrollTopRef.current = 0;

      return;
    }

    /*
     * Normal initial/date load.
     *
     * Latest message should be at the bottom.
     */
    container.scrollTop = container.scrollHeight;
  }, [messages, loadingChat]);

  /* =========================================================
     5. LOAD OLDER MESSAGES WHEN SCROLLING UP
  ========================================================= */

  const loadOlderMessages = useCallback(async () => {
    /*
     * Prevent duplicate requests.
     */
    if (
      isLoadingOlderRef.current ||
      loadingChat ||
      !selectedDate
    ) {
      return;
    }

    /*
     * Backend is DESC:
     *
     * page 1 = newest 50
     * page 2 = older messages
     * page 3 = even older messages
     */
    if (chatPage >= chatTotalPages) {
      return;
    }

    const container = messagesContainerRef.current;

    if (!container) {
      return;
    }

    /*
     * Save the current position BEFORE
     * inserting older messages.
     */
    previousScrollHeightRef.current =
      container.scrollHeight;

    previousScrollTopRef.current =
      container.scrollTop;

    isLoadingOlderRef.current = true;

    const nextPage = chatPage + 1;

    try {
      await loadChatForDate(
        selectedDate,
        nextPage,
        "appendOlder",
      );
    } finally {
      isLoadingOlderRef.current = false;
    }
  }, [
    loadingChat,
    selectedDate,
    chatPage,
    chatTotalPages,
    loadChatForDate,
  ]);

  /* =========================================================
     6. CHAT SCROLL HANDLER

     User scrolls UP
       ↓
     near top
       ↓
     load next backend page
       ↓
     older messages inserted at TOP
  ========================================================= */

  const handleChatScroll = useCallback(
    (event) => {
      const container = event.currentTarget;

      const nearTop =
        container.scrollTop <= 80;

      if (nearTop) {
        loadOlderMessages();
      }
    },
    [loadOlderMessages],
  );

  /* =========================================================
     7. LOAD OTHER CHAT DATES

     This API is intentionally NOT called automatically.
  ========================================================= */

  const handleShowOtherDates = async () => {
    /*
     * Hide if already visible.
     */
    if (showOtherDates) {
      setShowOtherDates(false);
      return;
    }

    /*
     * Already loaded once.
     */
    if (datesLoaded) {
      setShowOtherDates(true);
      return;
    }

    if (!userId) {
      toast.error("User information is unavailable.");

      return;
    }

    setShowOtherDates(true);
    setLoadingDates(true);

    try {
      const res = await getUserBotChatDates(userId);

      const isSuccess = res?.result === 1;

      if (!isSuccess) {
        toast.error(
          res?.error_value ||
            "Unable to load conversation dates.",
        );

        setChatDates([]);

        return;
      }

      const rawDates = Array.isArray(
        res?.result_value,
      )
        ? res.result_value
        : [];

      const normalizedDates = rawDates
        .map((entry) =>
          formatDateOnly(
            entry?.chatDate ?? entry,
          ),
        )
        .filter(Boolean);

      const uniqueDates = [
        ...new Set(normalizedDates),
      ].sort(
        (a, b) =>
          new Date(`${b}T00:00:00`) -
          new Date(`${a}T00:00:00`),
      );

      setChatDates(uniqueDates);
      setDatesLoaded(true);
    } catch (error) {
      console.error(
        "Failed to load conversation dates:",
        error,
      );

      toast.error(
        "Unable to load conversation dates.",
      );

      setChatDates([]);
    } finally {
      setLoadingDates(false);
    }
  };

  /* =========================================================
     8. SELECT ANOTHER DATE
  ========================================================= */

  const handleDateChange = async (date) => {
    if (
      !date ||
      date === selectedDate ||
      loadingChat
    ) {
      return;
    }

    setSelectedDate(date);

    setMessages([]);

    setChatPage(1);

    setChatTotalPages(1);

    /*
     * Reset scroll restoration state.
     */
    previousScrollHeightRef.current = 0;
    previousScrollTopRef.current = 0;

    /*
     * Load first page for the selected date.
     *
     * Backend returns newest → oldest.
     * loadChatForDate reverses it.
     */
    await loadChatForDate(
      date,
      1,
      "replace",
    );
  };

  /* =========================================================
     9. INVALID CLIENT
  ========================================================= */

  const renderInvalidClient = () => {
    return (
      <section className="cd-client-card cd-state">
        <div className="cd-state-icon">
          !
        </div>

        <h2>Client not found</h2>

        <p>
          We couldn't find client details for this
          user. The client may have been removed, or
          the detection information may no longer be
          available.
        </p>

        <div className="cd-invalid-meta">
          {userId && (
            <span>
              <strong>User ID</strong>{" "}
              {userId}
            </span>
          )}

          {detectionDate && (
            <span>
              <strong>Detection</strong>{" "}
              {formatDate(detectionDate)}
            </span>
          )}
        </div>

        <button
          type="button"
          className="cd-primary-btn"
          onClick={() => navigate(-1)}
        >
          ← Go back
        </button>
      </section>
    );
  };

  /* =========================================================
     PAGE
  ========================================================= */

  return (
    <div className="cd-page">
      {/* =====================================================
          HEADER
      ===================================================== */}

      <header className="cd-header">
        <div className="cd-header-left">
          <button
            type="button"
            className="cd-back-btn"
            onClick={() => navigate(-1)}
          >
            ← Back
          </button>

          <div className="cd-header-title">
            <h1>Client details</h1>

            <p>
              Client information and conversation
              history
            </p>
          </div>
        </div>

        {userId && (
          <div className="cd-user-badge">
            User ID {userId}
          </div>
        )}
      </header>

      {/* =====================================================
          MAIN CONTENT
      ===================================================== */}

      <main className="cd-content">
        {/* ===================================================
            CLIENT LOADING
        =================================================== */}

        {loadingClient ? (
          <section className="cd-client-card cd-loading-panel">
            <div className="cd-loader" />

            <div>
              <h2>
                Loading client details…
              </h2>

              <p>
                Please wait while we retrieve the
                client information.
              </p>
            </div>
          </section>
        ) : clientError ? (
          /*
           * IMPORTANT:
           * Header is still visible because
           * we DON'T return early.
           */
          renderInvalidClient()
        ) : (
          <>
            {/* =================================================
                CLIENT INFORMATION
            ================================================= */}

            <section className="cd-client-card">
              <div className="cd-section-title">
                <div>
                  <h2>
                    Client information
                  </h2>

                  <p>
                    Details captured when this
                    client was detected.
                  </p>
                </div>

                <span className="cd-detected-badge">
                  Client detected
                </span>
              </div>

              <div className="cd-client-grid">
                <div className="cd-info-item">
                  <span>Name</span>

                  <strong
                    className={
                      expandedFields.name
                        ? "cd-info-value cd-info-value--expanded"
                        : "cd-info-value"
                    }
                    title="Click to expand or collapse"
                    onClick={() => toggleFieldExpanded("name")}
                  >
                    {client?.name || "—"}
                  </strong>
                </div>

                <div className="cd-info-item">
                  <span>Contact</span>

                  <strong
                    className={
                      expandedFields.contact
                        ? "cd-info-value cd-info-value--expanded"
                        : "cd-info-value"
                    }
                    title="Click to expand or collapse"
                    onClick={() => toggleFieldExpanded("contact")}
                  >
                    {client?.contact || "—"}
                  </strong>
                </div>

                <div className="cd-info-item">
                  <span>
                    Client requirement
                  </span>

                  <strong
                    className={
                      expandedFields.requirement
                        ? "cd-info-value cd-info-value--expanded"
                        : "cd-info-value"
                    }
                    title="Click to expand or collapse"
                    onClick={() => toggleFieldExpanded("requirement")}
                  >
                    {client?.requirement ||
                      "No requirement provided."}
                  </strong>
                </div>

                <div className="cd-info-item">
                  <span>
                    Detected at
                  </span>

                  <strong
                    className={
                      expandedFields.detectedAt
                        ? "cd-info-value cd-info-value--expanded"
                        : "cd-info-value"
                    }
                    title="Click to expand or collapse"
                    onClick={() => toggleFieldExpanded("detectedAt")}
                  >
                    {formatDate(
                      client?.created_at,
                    )}
                  </strong>
                </div>
              </div>
            </section>

            {/* =================================================
                CONVERSATION
            ================================================= */}

            <section className="cd-chat-card">
              {/* CHAT HEADER */}

              <div className="cd-chat-header">
                <div>
                  <h2>
                    Conversation history
                  </h2>

                  <p>
                    Showing the conversation from
                    the selected date.
                  </p>
                </div>

                <div className="cd-chat-context">
                  <span>
                    Client detection
                  </span>

                  <strong>
                    {formatDate(
                      detectionDate,
                    )}
                  </strong>
                </div>
              </div>

              {/* =================================================
                  OTHER DATES BUTTON
              ================================================= */}

              <div className="cd-other-dates-bar">
                <div className="cd-other-dates-info">
                  <span
                    className="cd-other-dates-icon"
                    aria-hidden="true"
                  >
                    📅
                  </span>

                  <div>
                    <strong>
                      Want to see other
                      conversations?
                    </strong>

                    <span>
                      Browse other dates from this
                      user's chat history.
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  className="cd-other-dates-btn"
                  onClick={
                    handleShowOtherDates
                  }
                  disabled={loadingDates}
                >
                  {loadingDates
                    ? "Loading dates…"
                    : showOtherDates
                      ? "Hide other dates"
                      : "View other chat dates"}
                </button>
              </div>

              {/* =================================================
                  CHAT CONTAINER
              ================================================= */}

              <div className="cd-chat-container">
                {/* =================================================
                    DATE SIDEBAR
                ================================================= */}

                {showOtherDates && (
                  <aside className="cd-chat-dates">
                    <div className="cd-dates-title">
                      <strong>
                        Conversation dates
                      </strong>

                      <span>
                        {chatDates.length}{" "}
                        {chatDates.length === 1
                          ? "date"
                          : "dates"}
                      </span>
                    </div>

                    {loadingDates ? (
                      <div className="cd-date-loading">
                        <div className="cd-small-loader" />

                        Loading dates…
                      </div>
                    ) : chatDates.length ===
                      0 ? (
                      <div className="cd-no-dates">
                        No other conversations
                        found.
                      </div>
                    ) : (
                      <div className="cd-date-list">
                        {chatDates.map(
                          (date) => {
                            const isDateDetectionDay =
                              date ===
                              detectionDay;

                            const isActive =
                              date ===
                              selectedDate;

                            return (
                              <button
                                key={date}
                                type="button"
                                className={[
                                  "cd-date-item",
                                  isActive
                                    ? "cd-date-item--active"
                                    : "",
                                  isDateDetectionDay
                                    ? "cd-date-item--detection"
                                    : "",
                                ]
                                  .filter(
                                    Boolean,
                                  )
                                  .join(" ")}
                                onClick={() =>
                                  handleDateChange(
                                    date,
                                  )
                                }
                                disabled={
                                  loadingChat
                                }
                              >
                                <span className="cd-date-main">
                                  {formatReadableDate(
                                    date,
                                  )}
                                </span>

                                {isDateDetectionDay && (
                                  <small>
                                    Client detected
                                  </small>
                                )}
                              </button>
                            );
                          },
                        )}
                      </div>
                    )}
                  </aside>
                )}

                {/* =================================================
                    CHAT AREA
                ================================================= */}

                <div className="cd-chat-messages">
                  {/* SELECTED DATE */}

                  <div className="cd-selected-date">
                    <div>
                      <span>
                        Showing conversation for
                      </span>

                      <strong>
                        {selectedDate
                          ? formatLongDate(
                              selectedDate,
                            )
                          : "—"}
                      </strong>
                    </div>

                    {isDetectionDate && (
                      <span className="cd-detection-label">
                        Client detection day
                      </span>
                    )}
                  </div>

                  {/* =================================================
                      MESSAGES
                  ================================================= */}

                  {chatError ? (
                    <div className="cd-chat-state">
                      <div className="cd-state-icon cd-state-icon--sm">
                        !
                      </div>

                      <h3>
                        Unable to load
                        conversation
                      </h3>

                      <p>
                        Something went wrong while
                        retrieving the chat for this
                        date.
                      </p>
                    </div>
                  ) : messages.length ===
                      0 &&
                    loadingChat ? (
                    <div className="cd-chat-state">
                      <div className="cd-loader" />

                      <h3>
                        Loading conversation…
                      </h3>

                      <p>
                        Retrieving messages for
                        this date.
                      </p>
                    </div>
                  ) : messages.length ===
                    0 ? (
                    <div className="cd-chat-state">
                      <div
                        className="cd-empty-icon"
                        aria-hidden="true"
                      >
                        💬
                      </div>

                      <h3>
                        No messages
                      </h3>

                      <p>
                        There are no conversations
                        recorded for this date.
                      </p>
                    </div>
                  ) : (
                    <div
                      ref={
                        messagesContainerRef
                      }
                      className="cd-messages-list"
                      onScroll={
                        handleChatScroll
                      }
                    >
                      {/* =================================================
                          LOADING OLDER MESSAGES
                      ================================================= */}

                      {loadingChat &&
                        messages.length >
                          0 && (
                          <div className="cd-loading-older">
                            <div className="cd-small-loader" />

                            Loading older
                            messages…
                          </div>
                        )}

                      {/* =================================================
                          MESSAGE LIST

                          messages is always:
                          OLDEST → NEWEST

                          Therefore the latest message
                          naturally appears at the bottom.
                      ================================================= */}

                      {messages.map(
                        (message, index) => {
                          const roleId =
                            message.sender_chatRole_id;

                          const isUser =
                            isUserMessage(
                              roleId,
                            );

                          const isBot =
                            isBotMessage(
                              roleId,
                            );

                          const isAgent =
                            isAgentMessage(
                              roleId,
                            );

                          /*
                           * User -> LEFT
                           * Bot  -> RIGHT
                           * Agent -> RIGHT
                           */

                          let rowClass =
                            "cd-message-row";

                          let bubbleClass =
                            "cd-message";

                          if (isUser) {
                            rowClass +=
                              " cd-message-row--user";

                            bubbleClass +=
                              " cd-message--user";
                          } else if (isBot) {
                            rowClass +=
                              " cd-message-row--bot";

                            bubbleClass +=
                              " cd-message--bot";
                          } else if (isAgent) {
                            rowClass +=
                              " cd-message-row--agent";

                            bubbleClass +=
                              " cd-message--agent";
                          } else {
                            rowClass +=
                              " cd-message-row--bot";

                            bubbleClass +=
                              " cd-message--bot";
                          }

                          return (
                            <div
                              key={
                                message.id ??
                                `${message.sended_at}-${index}`
                              }
                              className={
                                rowClass
                              }
                            >
                              <div
                                className={
                                  bubbleClass
                                }
                              >
                                <div className="cd-message-top">
                                  <span className="cd-role">
                                    {getRoleName(
                                      roleId,
                                    )}
                                  </span>

                                  <span className="cd-message-time">
                                    {formatTimeOnly(
                                      message.sended_at,
                                    )}
                                  </span>
                                </div>

                                <div className="cd-message-text">
                                  <MarkdownMessage text={message.msg} />
                                </div>
                              </div>
                            </div>
                          );
                        },
                      )}
                    </div>
                  )}
                </div>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

